import { type ReactElement, type Dispatch, type SetStateAction, type ReactNode } from 'react'
import { cn } from '@util'
import { isNotDefined, isTrue } from '@terros/common'
import { Divider, Stack, Transition, type StackProps } from '@mantine/core'
import { Size } from '@front/model'
import { MaterialSymbolIcon } from '../icon'
import { SectionHeading, SectionHeadingTitle, type SectionHeadingProps } from './SectionHeading'

type CollapsibleProps = {
  isCollapsed: boolean
  setIsCollapsed: Dispatch<SetStateAction<boolean>>
}

export type TSectionProps = StackProps & {
  heading: SectionHeadingProps
  contentProps?: StackProps
  collapsibleProps?: CollapsibleProps
  fixedChildren?: ReactNode
}

export const TSection = ({
  heading,
  children,
  contentProps,
  collapsibleProps,
  fixedChildren,
  ...props
}: TSectionProps): ReactElement => {
  return (
    <Stack gap={Size.space.xl} {...props}>
      <Heading heading={heading} collapsibleProps={collapsibleProps} />
      <Divider />
      <Content
        children={children}
        fixedChildren={fixedChildren}
        collapsibleProps={collapsibleProps}
        contentProps={{ ...contentProps, style: { padding: Size.space['2xl'], ...contentProps?.style } }}
      />
    </Stack>
  )
}

const Content = ({
  children,
  contentProps,
  fixedChildren,
  collapsibleProps,
}: Pick<TSectionProps, 'children' | 'contentProps' | 'fixedChildren' | 'collapsibleProps'>): ReactElement => {
  if (isNotDefined(fixedChildren))
    return (
      <Transition mounted={!isTrue(collapsibleProps?.isCollapsed)} transition='scale-y' duration={200}>
        {(styles) => (
          <div style={styles}>
            <Stack gap={Size.space['3xl']} {...contentProps}>
              {children}
            </Stack>
          </div>
        )}
      </Transition>
    )
  return (
    <Stack gap={Size.space['3xl']} {...contentProps}>
      {fixedChildren}
      <Content children={children} contentProps={contentProps} collapsibleProps={collapsibleProps} />
    </Stack>
  )
}
const Heading = ({
  heading,
  collapsibleProps,
}: {
  heading: SectionHeadingProps
  collapsibleProps?: CollapsibleProps
}): ReactElement => {
  if (isNotDefined(collapsibleProps)) return <SectionHeading {...heading} />

  return (
    <SectionHeading
      {...heading}
      title={<CollapsibleHeading title={heading.title} collapsibleProps={collapsibleProps} />}
      children={collapsibleProps.isCollapsed ? undefined : heading.children}
    />
  )
}

const CollapsibleHeading = ({
  title,
  collapsibleProps,
}: {
  title: string | ReactNode
  collapsibleProps?: CollapsibleProps
}): ReactElement => {
  return (
    <button
      onClick={() => collapsibleProps?.setIsCollapsed((prev) => !prev)}
      type='button'
      className='flex w-full items-center gap-md'
    >
      <div className='flex-1'>
        <SectionHeadingTitle title={title} />
      </div>
      <MaterialSymbolIcon
        icon='keyboard_arrow_up'
        size='xl'
        className={cn(
          'transition-transform duration-200',
          isTrue(collapsibleProps?.isCollapsed) ? 'rotate-180' : undefined
        )}
      />
    </button>
  )
}
